import React, { useCallback, useEffect, useState } from "react";
import { TextField, Button, Typography, FormHelperText, Box, RadioGroup, Radio, FormControlLabel, Grid, InputAdornment } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { get, put } from "../../../utils/api";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import styles from "../../../styles/social/social.module.css";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import SocialLayout from "../../../Layout/SocialLayout";
import { textError } from "../../../utils/validationMessage";
import { fixedPercentage } from "../../../utils/common";
import SelectField from "../../../Components/Masters/SelectField";
import { monthlist } from "../../../utils/constant";
import Diversity from "./Diversity";
import MessagePopup from "../../../Components/Masters/MessagePopup";

const NumericInputField = ({
  label,
  name,
  errors,
  value,
  onChange,
  placeholder,
  disabled,
  width = "100%",
  endAdornment
}) => (
  <div style={{ marginBottom: "1rem" }}>
    <p className={styles.form_heading} >{label}</p>
    <TextField
      name={name}
      value={value}
      onChange={onChange}
      variant="outlined"
      size="small"
      sx={{ pl: 2, width: width }}
      placeholder={placeholder}
      error={!!errors[name]}
      disabled={disabled}
      InputProps={{
        endAdornment: endAdornment
      }}
    />
    {errors[name] && (
      <FormHelperText
        sx={{ color: "#D34040", marginLeft: "1rem", fontSize: "14px" }}
      >
        {errors[name]}
      </FormHelperText>
    )}
  </div>
);

const WellBeing = () => {
  const SOCIAL_PRIMARY = "#1C486B";
  const socialRadioSx = {
    color: SOCIAL_PRIMARY,
    "&.Mui-checked": { color: SOCIAL_PRIMARY },
  };
  const primaryButtonSx = {
    textTransform: "none",
    backgroundColor: SOCIAL_PRIMARY,
    "&:hover": { backgroundColor: "#163a56" },
  };
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [errors, setErrors] = useState({});
  const [module, setModule] = useState(searchParams.get("module") || "monthly");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const startYear = currentYear - 1;
  const years = Array.from({ length: 2 }, (_, i) => startYear + i);
  const [filteredYears, setFilteredYears] = useState(years);
  const [disableValue, setDisableValue] = useState("");

  // Monthly formData
  const monthlyFormDataInitial = {
    // Section 1: Total Number of Employees
    // i. Permanent employees
    permEmpMale: "",
    permEmpDifferentlyAbledMale: "",
    permEmpFemale: "",
    permEmpDifferentlyAbledFemale: "",
    permEmpTotal: "",
    // ii. Temporary Employees
    tempEmpMale: "",
    tempEmpDifferentlyAbledMale: "",
    tempEmpFemale: "",
    tempEmpDifferentlyAbledFemale: "",
    tempEmpTotal: "",
    // iii. Permanent Workers
    permWorkerMale: "",
    permWorkerDifferentlyAbledMale: "",
    permWorkerFemale: "",
    permWorkerDifferentlyAbledFemale: "",
    permWorkerTotal: "",
    // iv. Temporary Workers
    tempWorkerMale: "",
    tempWorkerDifferentlyAbledMale: "",
    tempWorkerFemale: "",
    tempWorkerDifferentlyAbledFemale: "",
    tempWorkerTotal: "",
    totalEmployees: "", // Sum of all four totals: i(e) + ii(e) + iii(e) + iv(e)
    // Section 2-7
    trainingHours: "",
    avgTrainingHoursPerEmployee: "",
    healthAndSafetyIncidents: "",
    ltiEmployees: "",
    ltiWorkers: "",
    workingHoursEmployees: "",
    workingHoursWorkers: "",
    ltifrEmployees: "",
    ltifrWorkers: "",
  };

  // Annual formData
  const annualFormDataInitial = {
    beginningYearPermEmpMale: "",
    beginningYearPermEmpFemale: "",
    beginningYearPermWorkerMale: "",
    beginningYearPermWorkerFemale: "",
    endYearPermEmpMale: "",
    endYearPermEmpFemale: "",
    endYearPermWorkerMale: "",
    endYearPermWorkerFemale: "",
    leftYearPermEmpMale: "",
    leftYearPermEmpFemale: "",
    leftYearPermWorkerMale: "",
    leftYearPermWorkerFemale: "",
    turnoverRatePermEmpMale: "",
    turnoverRatePermEmpFemale: "",
    turnoverRatePermWorkerMale: "",
    turnoverRatePermWorkerFemale: "",
  };

  const [monthlyFormData, setMonthlyFormData] = useState(monthlyFormDataInitial);
  const [annualFormData, setAnnualFormData] = useState(annualFormDataInitial);

  // Get the appropriate formData based on module
  const formData = module === "annual" ? annualFormData : monthlyFormData;
  const setFormData = module === "annual" ? setAnnualFormData : setMonthlyFormData;
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1); // Step 1: WellBeing, Step 2: Diversity
  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const removeCommas = (val) => {
    if (val === null || val === undefined) return "";
    return val.toString().replace(/,/g, "").trim();
  };

  const formatWithCommas = (val) => {
    const normalized = removeCommas(val);
    if (normalized === "") return "";
    if (!/^\d*\.?\d*$/.test(normalized)) return normalized;

    const [integerPart = "", decimalPart] = normalized.split(".");
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return decimalPart !== undefined
      ? `${formattedInteger}.${decimalPart}`
      : formattedInteger;
  };

  // Helper function to safely parse number values
  const parseNumber = (val) => {
    if (!val || val === "" || val === null || val === undefined) return 0;
    const parsed = parseFloat(removeCommas(val));
    return isNaN(parsed) ? 0 : parsed;
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    console.log(name, value, "changedValue");

    const normalizedValue = removeCommas(value);
    if (!/^\d*\.?\d*$/.test(normalizedValue)) return;
    const formattedValue = formatWithCommas(normalizedValue);

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    if (module === "annual") {
      setAnnualFormData((prev) => {
        const updated = {
          ...prev,
          [name]: formattedValue,
        };

        // Annual calculations - turnover rate depends on sections 1, 2 and 3
        const annualFields = [
          "beginningYearPermEmpMale", "beginningYearPermEmpFemale",
          "beginningYearPermWorkerMale", "beginningYearPermWorkerFemale",
          "endYearPermEmpMale", "endYearPermEmpFemale",
          "endYearPermWorkerMale", "endYearPermWorkerFemale",
          "leftYearPermEmpMale", "leftYearPermEmpFemale",
          "leftYearPermWorkerMale", "leftYearPermWorkerFemale"
        ];

        if (annualFields.includes(name)) {
          const begEmpMale = parseNumber(updated.beginningYearPermEmpMale);
          const endEmpMale = parseNumber(updated.endYearPermEmpMale);
          const begEmpFemale = parseNumber(updated.beginningYearPermEmpFemale);
          const endEmpFemale = parseNumber(updated.endYearPermEmpFemale);
          const begWorkerMale = parseNumber(updated.beginningYearPermWorkerMale);
          const endWorkerMale = parseNumber(updated.endYearPermWorkerMale);
          const begWorkerFemale = parseNumber(updated.beginningYearPermWorkerFemale);
          const endWorkerFemale = parseNumber(updated.endYearPermWorkerFemale);

          const leftEmpMale = parseNumber(updated.leftYearPermEmpMale);
          const avgEmpMale = (endEmpMale + begEmpMale) / 2;
          if (avgEmpMale > 0) {
            updated.turnoverRatePermEmpMale = formatWithCommas(((leftEmpMale / avgEmpMale) * 100).toFixed(2));
          } else {
            updated.turnoverRatePermEmpMale = "0";
          }

          const leftEmpFemale = parseNumber(updated.leftYearPermEmpFemale);
          const avgEmpFemale = (endEmpFemale + begEmpFemale) / 2;
          if (avgEmpFemale > 0) {
            updated.turnoverRatePermEmpFemale = formatWithCommas(((leftEmpFemale / avgEmpFemale) * 100).toFixed(2));
          } else {
            updated.turnoverRatePermEmpFemale = "0";
          }

          const leftWorkerMale = parseNumber(updated.leftYearPermWorkerMale);
          const avgWorkerMale = (endWorkerMale + begWorkerMale) / 2;
          if (avgWorkerMale > 0) {
            updated.turnoverRatePermWorkerMale = formatWithCommas(((leftWorkerMale / avgWorkerMale) * 100).toFixed(2));
          } else {
            updated.turnoverRatePermWorkerMale = "0";
          }

          const leftWorkerFemale = parseNumber(updated.leftYearPermWorkerFemale);
          const avgWorkerFemale = (endWorkerFemale + begWorkerFemale) / 2;
          if (avgWorkerFemale > 0) {
            updated.turnoverRatePermWorkerFemale = formatWithCommas(((leftWorkerFemale / avgWorkerFemale) * 100).toFixed(2));
          } else {
            updated.turnoverRatePermWorkerFemale = "0";
          }
        }

        // Real-time validation: Check if 1(a) > 2(a), etc.
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };

          // Validate 2(a): endYearPermEmpMale must be >= beginningYearPermEmpMale
          const begEmpMale = parseNumber(updated.beginningYearPermEmpMale);
          const endEmpMale = parseNumber(updated.endYearPermEmpMale);
          const hasBegEmpMale =
            String(updated.beginningYearPermEmpMale ?? "").trim() !== "";
          const hasEndEmpMale =
            String(updated.endYearPermEmpMale ?? "").trim() !== "";
          // if (hasBegEmpMale && hasEndEmpMale && begEmpMale > endEmpMale) {
          //   newErrors.endYearPermEmpMale = "End of year value must be greater than or equal to beginning of year value";
          // } else {
          //   delete newErrors.endYearPermEmpMale;
          // }

          // Validate 2(b): endYearPermEmpFemale must be >= beginningYearPermEmpFemale
          const begEmpFemale = parseNumber(updated.beginningYearPermEmpFemale);
          const endEmpFemale = parseNumber(updated.endYearPermEmpFemale);
          const hasBegEmpFemale =
            String(updated.beginningYearPermEmpFemale ?? "").trim() !== "";
          const hasEndEmpFemale =
            String(updated.endYearPermEmpFemale ?? "").trim() !== "";
          // if (hasBegEmpFemale && hasEndEmpFemale && begEmpFemale > endEmpFemale) {
          //   newErrors.endYearPermEmpFemale = "End of year value must be greater than or equal to beginning of year value";
          // } else {
          //   delete newErrors.endYearPermEmpFemale;
          // }

          // Validate 2(c): endYearPermWorkerMale must be >= beginningYearPermWorkerMale
          const begWorkerMale = parseNumber(updated.beginningYearPermWorkerMale);
          const endWorkerMale = parseNumber(updated.endYearPermWorkerMale);
          const hasBegWorkerMale =
            String(updated.beginningYearPermWorkerMale ?? "").trim() !== "";
          const hasEndWorkerMale =
            String(updated.endYearPermWorkerMale ?? "").trim() !== "";
          // if (hasBegWorkerMale && hasEndWorkerMale && begWorkerMale > endWorkerMale) {
          //   newErrors.endYearPermWorkerMale = "End of year value must be greater than or equal to beginning of year value";
          // } else {
          //   delete newErrors.endYearPermWorkerMale;
          // }

          // Validate 2(d): endYearPermWorkerFemale must be >= beginningYearPermWorkerFemale
          const begWorkerFemale = parseNumber(updated.beginningYearPermWorkerFemale);
          const endWorkerFemale = parseNumber(updated.endYearPermWorkerFemale);
          const hasBegWorkerFemale =
            String(updated.beginningYearPermWorkerFemale ?? "").trim() !== "";
          const hasEndWorkerFemale =
            String(updated.endYearPermWorkerFemale ?? "").trim() !== "";
          // if (
          //   hasBegWorkerFemale &&
          //   hasEndWorkerFemale &&
          //   begWorkerFemale > endWorkerFemale
          // ) {
          //   newErrors.endYearPermWorkerFemale = "End of year value must be greater than or equal to beginning of year value";
          // } 
          // else {
          //   delete newErrors.endYearPermWorkerFemale;
          // }

          return newErrors;
        });

        return updated;
      });
    } else {
      setMonthlyFormData((prev) => {
        const updated = {
          ...prev,
          [name]: formattedValue,
        };

        // Calculate totals for Permanent Employees (a+b+c+d)
        if (["permEmpMale", "permEmpDifferentlyAbledMale", "permEmpFemale", "permEmpDifferentlyAbledFemale"].includes(name)) {
          const a = parseNumber(updated.permEmpMale);
          const b = parseNumber(updated.permEmpDifferentlyAbledMale);
          const c = parseNumber(updated.permEmpFemale);
          const d = parseNumber(updated.permEmpDifferentlyAbledFemale);
          updated.permEmpTotal = formatWithCommas((a + b + c + d).toString());
        }

        // Calculate totals for Temporary Employees (a+b+c+d)
        if (["tempEmpMale", "tempEmpDifferentlyAbledMale", "tempEmpFemale", "tempEmpDifferentlyAbledFemale"].includes(name)) {
          const a = parseNumber(updated.tempEmpMale);
          const b = parseNumber(updated.tempEmpDifferentlyAbledMale);
          const c = parseNumber(updated.tempEmpFemale);
          const d = parseNumber(updated.tempEmpDifferentlyAbledFemale);
          updated.tempEmpTotal = formatWithCommas((a + b + c + d).toString());
        }

        // Calculate totals for Permanent Workers (a+b+c+d)
        if (["permWorkerMale", "permWorkerDifferentlyAbledMale", "permWorkerFemale", "permWorkerDifferentlyAbledFemale"].includes(name)) {
          const a = parseNumber(updated.permWorkerMale);
          const b = parseNumber(updated.permWorkerDifferentlyAbledMale);
          const c = parseNumber(updated.permWorkerFemale);
          const d = parseNumber(updated.permWorkerDifferentlyAbledFemale);
          updated.permWorkerTotal = formatWithCommas((a + b + c + d).toString());
        }

        // Calculate totals for Temporary Workers (a+b+c+d)
        if (["tempWorkerMale", "tempWorkerDifferentlyAbledMale", "tempWorkerFemale", "tempWorkerDifferentlyAbledFemale"].includes(name)) {
          const a = parseNumber(updated.tempWorkerMale);
          const b = parseNumber(updated.tempWorkerDifferentlyAbledMale);
          const c = parseNumber(updated.tempWorkerFemale);
          const d = parseNumber(updated.tempWorkerDifferentlyAbledFemale);
          updated.tempWorkerTotal = formatWithCommas((a + b + c + d).toString());
        }

        // Calculate total employees: i(e) + ii(e) + iii(e) + iv(e)
        // Recalculate whenever any component field changes (which updates the totals)
        if (["permEmpMale", "permEmpDifferentlyAbledMale", "permEmpFemale", "permEmpDifferentlyAbledFemale",
          "tempEmpMale", "tempEmpDifferentlyAbledMale", "tempEmpFemale", "tempEmpDifferentlyAbledFemale",
          "permWorkerMale", "permWorkerDifferentlyAbledMale", "permWorkerFemale", "permWorkerDifferentlyAbledFemale",
          "tempWorkerMale", "tempWorkerDifferentlyAbledMale", "tempWorkerFemale", "tempWorkerDifferentlyAbledFemale"].includes(name)) {
          // First ensure all individual totals are calculated
          const permEmpA = parseNumber(updated.permEmpMale);
          const permEmpB = parseNumber(updated.permEmpDifferentlyAbledMale);
          const permEmpC = parseNumber(updated.permEmpFemale);
          const permEmpD = parseNumber(updated.permEmpDifferentlyAbledFemale);
          updated.permEmpTotal = formatWithCommas((permEmpA + permEmpB + permEmpC + permEmpD).toString());

          const tempEmpA = parseNumber(updated.tempEmpMale);
          const tempEmpB = parseNumber(updated.tempEmpDifferentlyAbledMale);
          const tempEmpC = parseNumber(updated.tempEmpFemale);
          const tempEmpD = parseNumber(updated.tempEmpDifferentlyAbledFemale);
          updated.tempEmpTotal = formatWithCommas((tempEmpA + tempEmpB + tempEmpC + tempEmpD).toString());

          const permWorkerA = parseNumber(updated.permWorkerMale);
          const permWorkerB = parseNumber(updated.permWorkerDifferentlyAbledMale);
          const permWorkerC = parseNumber(updated.permWorkerFemale);
          const permWorkerD = parseNumber(updated.permWorkerDifferentlyAbledFemale);
          updated.permWorkerTotal = formatWithCommas((permWorkerA + permWorkerB + permWorkerC + permWorkerD).toString());

          const tempWorkerA = parseNumber(updated.tempWorkerMale);
          const tempWorkerB = parseNumber(updated.tempWorkerDifferentlyAbledMale);
          const tempWorkerC = parseNumber(updated.tempWorkerFemale);
          const tempWorkerD = parseNumber(updated.tempWorkerDifferentlyAbledFemale);
          updated.tempWorkerTotal = formatWithCommas((tempWorkerA + tempWorkerB + tempWorkerC + tempWorkerD).toString());

          // Now calculate grand total: i(e) + ii(e) + iii(e) + iv(e)
          const iTotal = parseNumber(updated.permEmpTotal);
          const iiTotal = parseNumber(updated.tempEmpTotal);
          const iiiTotal = parseNumber(updated.permWorkerTotal);
          const ivTotal = parseNumber(updated.tempWorkerTotal);
          updated.totalEmployees = formatWithCommas((iTotal + iiTotal + iiiTotal + ivTotal).toString());
        }

        // Calculate LTIFR for Employees: 5(a)/6(a)*1000000
        if (name === "workingHoursEmployees" || name === "ltiEmployees") {
          const workingHours = parseNumber(updated.workingHoursEmployees);
          const LTI = parseNumber(updated.ltiEmployees);
          if (workingHours > 0 && LTI >= 0) {
            updated.ltifrEmployees = formatWithCommas(((LTI / workingHours) * 1000000).toFixed(2));
          } else {
            updated.ltifrEmployees = "0";
          }
        }

        // Calculate LTIFR for Workers: 5(c)/6(c)*1000000
        if (name === "workingHoursWorkers" || name === "ltiWorkers") {
          const workingHours = parseNumber(updated.workingHoursWorkers);
          const LTI = parseNumber(updated.ltiWorkers);
          if (workingHours > 0 && LTI >= 0) {
            updated.ltifrWorkers = formatWithCommas(((LTI / workingHours) * 1000000).toFixed(2));
          } else {
            updated.ltifrWorkers = "0";
          }
        }

        return updated;
      });
    }
  };

  const fetchData = useCallback(async (id) => {
    try {
      const response = await get(`/social/getSocial?id=${id}`);
      // Handle array response - get first item if array, otherwise use directly
      const responseData = response?.data?.data;
      const socialData = Array.isArray(responseData) ? responseData[0] : responseData;
      let data = socialData?.employeeWellBeing;

      // Get module from URL params, API response, or default to monthly
      // const urlModule = searchParams.get("entryType");
      const apiModule = socialData?.module || socialData?._socialDataRef?.module;
      let finalModule = "monthly";

      console.log(apiModule, response, "apiModule");

      if ( apiModule === "Annual" || apiModule === "annual") {
        finalModule = "annual";
      } else if ( apiModule === "Monthly" || apiModule === "monthly") {
        finalModule = "monthly";
      }

      console.log(finalModule, apiModule, "finalModule");
      
      setModule(finalModule);

      // Set month and year from yearToSubmit or direct properties
      const yearToSubmit = socialData?._socialDataRef?.yearToSubmit;
      const monthToSubmit = socialData?._socialDataRef?.monthToSubmit;
      if (finalModule === "annual") {
        // For annual module, yearToSubmit should be a single year value
        if (yearToSubmit) {
          setYear(yearToSubmit.toString());
        }
      } else {
        // Monthly → set both year and month
        if (yearToSubmit) {
          setYear(yearToSubmit.toString());
        }

        if (monthToSubmit !== undefined && monthToSubmit !== null) {
          setMonth(monthToSubmit.toString());
        }

        // Disable future year logic
        const selectedMonth = monthToSubmit;
        if (selectedMonth > currentMonth + 1 && yearToSubmit === currentYear) {
          setDisableValue(currentYear);
        }
      }

      // Persist basic selection info in localStorage for use across Social modules/layout
      try {
        const socialEntryMeta = {
          module: finalModule, // "annual" or "monthly"
        };
        localStorage.setItem(
          "socialEntryMeta",
          JSON.stringify(socialEntryMeta)
        );
      } catch (e) {
        console.error("Failed to persist socialEntryMeta in localStorage", e);
      }

      console.log(data, "values of the FetchData");

      // Separate monthly and annual data
      const monthlyData = {};
      const annualData = {};

      // Monthly fields
      const monthlyFields = [
        "permEmpMale", "permEmpDifferentlyAbledMale", "permEmpFemale", "permEmpDifferentlyAbledFemale", "permEmpTotal",
        "tempEmpMale", "tempEmpDifferentlyAbledMale", "tempEmpFemale", "tempEmpDifferentlyAbledFemale", "tempEmpTotal",
        "permWorkerMale", "permWorkerDifferentlyAbledMale", "permWorkerFemale", "permWorkerDifferentlyAbledFemale", "permWorkerTotal",
        "tempWorkerMale", "tempWorkerDifferentlyAbledMale", "tempWorkerFemale", "tempWorkerDifferentlyAbledFemale", "tempWorkerTotal",
        "trainingHours", "avgTrainingHoursPerEmployee", "healthAndSafetyIncidents",
        "ltiEmployees", "ltiWorkers", "workingHoursEmployees", "workingHoursWorkers",
        "ltifrEmployees", "ltifrWorkers"
      ];

      // Annual fields
      const annualFields = [
        "beginningYearPermEmpMale", "beginningYearPermEmpFemale", "beginningYearPermWorkerMale", "beginningYearPermWorkerFemale",
        "endYearPermEmpMale", "endYearPermEmpFemale", "endYearPermWorkerMale", "endYearPermWorkerFemale",
        "leftYearPermEmpMale", "leftYearPermEmpFemale", "leftYearPermWorkerMale", "leftYearPermWorkerFemale",
        "turnoverRatePermEmpMale", "turnoverRatePermEmpFemale", "turnoverRatePermWorkerMale", "turnoverRatePermWorkerFemale"
      ];

      if (data) {
        Object.keys(data).forEach(key => {
          if (monthlyFields.includes(key)) {
            monthlyData[key] = formatWithCommas(data[key]);
          } else if (annualFields.includes(key)) {
            annualData[key] = formatWithCommas(data[key]);
          }
        });
      }

      setMonthlyFormData((prevData) => {
        const updated = {
          ...prevData,
          ...monthlyData,
        };

        // Recalculate totals after loading data
        // Calculate Permanent Employees Total (a+b+c+d)
        const permEmpA = parseNumber(updated.permEmpMale);
        const permEmpB = parseNumber(updated.permEmpDifferentlyAbledMale);
        const permEmpC = parseNumber(updated.permEmpFemale);
        const permEmpD = parseNumber(updated.permEmpDifferentlyAbledFemale);
        updated.permEmpTotal = formatWithCommas((permEmpA + permEmpB + permEmpC + permEmpD).toString());

        // Calculate Temporary Employees Total (a+b+c+d)
        const tempEmpA = parseNumber(updated.tempEmpMale);
        const tempEmpB = parseNumber(updated.tempEmpDifferentlyAbledMale);
        const tempEmpC = parseNumber(updated.tempEmpFemale);
        const tempEmpD = parseNumber(updated.tempEmpDifferentlyAbledFemale);
        updated.tempEmpTotal = formatWithCommas((tempEmpA + tempEmpB + tempEmpC + tempEmpD).toString());

        // Calculate Permanent Workers Total (a+b+c+d)
        const permWorkerA = parseNumber(updated.permWorkerMale);
        const permWorkerB = parseNumber(updated.permWorkerDifferentlyAbledMale);
        const permWorkerC = parseNumber(updated.permWorkerFemale);
        const permWorkerD = parseNumber(updated.permWorkerDifferentlyAbledFemale);
        updated.permWorkerTotal = formatWithCommas((permWorkerA + permWorkerB + permWorkerC + permWorkerD).toString());

        // Calculate Temporary Workers Total (a+b+c+d)
        const tempWorkerA = parseNumber(updated.tempWorkerMale);
        const tempWorkerB = parseNumber(updated.tempWorkerDifferentlyAbledMale);
        const tempWorkerC = parseNumber(updated.tempWorkerFemale);
        const tempWorkerD = parseNumber(updated.tempWorkerDifferentlyAbledFemale);
        updated.tempWorkerTotal = formatWithCommas((tempWorkerA + tempWorkerB + tempWorkerC + tempWorkerD).toString());

        // Calculate total employees: i(e) + ii(e) + iii(e) + iv(e)
        const iTotal = parseNumber(updated.permEmpTotal);
        const iiTotal = parseNumber(updated.tempEmpTotal);
        const iiiTotal = parseNumber(updated.permWorkerTotal);
        const ivTotal = parseNumber(updated.tempWorkerTotal);
        updated.totalEmployees = formatWithCommas((iTotal + iiTotal + iiiTotal + ivTotal).toString());

        // Calculate LTIFR for Employees: 5(a)/6(a)*1000000
        const workingHoursEmp = parseNumber(updated.workingHoursEmployees);
        const ltiEmp = parseNumber(updated.ltiEmployees);
        if (workingHoursEmp > 0 && ltiEmp >= 0) {
          updated.ltifrEmployees = formatWithCommas(((ltiEmp / workingHoursEmp) * 1000000).toFixed(2));
        } else {
          updated.ltifrEmployees = "0";
        }

        // Calculate LTIFR for Workers: 5(c)/6(c)*1000000
        const workingHoursWkr = parseNumber(updated.workingHoursWorkers);
        const ltiWkr = parseNumber(updated.ltiWorkers);
        if (workingHoursWkr > 0 && ltiWkr >= 0) {
          updated.ltifrWorkers = formatWithCommas(((ltiWkr / workingHoursWkr) * 1000000).toFixed(2));
        } else {
          updated.ltifrWorkers = "0";
        }

        return updated;
      });

      setAnnualFormData((prevData) => {
        const updated = {
          ...prevData,
          ...annualData,
        };

        const begEmpMale = parseNumber(updated.beginningYearPermEmpMale);
        const endEmpMale = parseNumber(updated.endYearPermEmpMale);
        const begEmpFemale = parseNumber(updated.beginningYearPermEmpFemale);
        const endEmpFemale = parseNumber(updated.endYearPermEmpFemale);
        const begWorkerMale = parseNumber(updated.beginningYearPermWorkerMale);
        const endWorkerMale = parseNumber(updated.endYearPermWorkerMale);
        const begWorkerFemale = parseNumber(updated.beginningYearPermWorkerFemale);
        const endWorkerFemale = parseNumber(updated.endYearPermWorkerFemale);

        const leftEmpMale = parseNumber(updated.leftYearPermEmpMale);
        const avgEmpMale = (endEmpMale + begEmpMale) / 2;
        if (avgEmpMale > 0) {
          updated.turnoverRatePermEmpMale = formatWithCommas(((leftEmpMale / avgEmpMale) * 100).toFixed(2));
        } else {
          updated.turnoverRatePermEmpMale = "0";
        }

        const leftEmpFemale = parseNumber(updated.leftYearPermEmpFemale);
        const avgEmpFemale = (endEmpFemale + begEmpFemale) / 2;
        if (avgEmpFemale > 0) {
          updated.turnoverRatePermEmpFemale = formatWithCommas(((leftEmpFemale / avgEmpFemale) * 100).toFixed(2));
        } else {
          updated.turnoverRatePermEmpFemale = "0";
        }

        const leftWorkerMale = parseNumber(updated.leftYearPermWorkerMale);
        const avgWorkerMale = (endWorkerMale + begWorkerMale) / 2;
        if (avgWorkerMale > 0) {
          updated.turnoverRatePermWorkerMale = formatWithCommas(((leftWorkerMale / avgWorkerMale) * 100).toFixed(2));
        } else {
          updated.turnoverRatePermWorkerMale = "0";
        }

        const leftWorkerFemale = parseNumber(updated.leftYearPermWorkerFemale);
        const avgWorkerFemale = (endWorkerFemale + begWorkerFemale) / 2;
        if (avgWorkerFemale > 0) {
          updated.turnoverRatePermWorkerFemale = formatWithCommas(((leftWorkerFemale / avgWorkerFemale) * 100).toFixed(2));
        } else {
          updated.turnoverRatePermWorkerFemale = "0";
        }

        return updated;
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchData(id);
  }, [id, fetchData]);

  // Persist module change to localStorage so dashboard/layout can use it
  const handleModuleChange = (event) => {
    const newModule = event.target.value;
    setModule(newModule);

    try {
      const existing =
        JSON.parse(localStorage.getItem("socialEntryMeta")) || {};

      const updated = {
        ...existing,
        module: newModule,
        // Prefer current state values if available
        year:
          year !== undefined && year !== null && year !== ""
            ? Number(year)
            : existing.year ?? null,
        month:
          month !== undefined && month !== null && month !== ""
            ? Number(month)
            : existing.month ?? null,
        id: existing.id || id,
      };

      localStorage.setItem("socialEntryMeta", JSON.stringify(updated));
    } catch (e) {
      console.error(
        "Failed to persist socialEntryMeta on module change",
        e
      );
    }
  };

  const validation = () => {
    let newErrors = {}; // Initialize an empty error object
    let hasErrors = false;

    // Fields to exclude from validation (calculated/auto fields)
    const excludedFields = ["averageTenure", "LTIFR", "turnoverRate", "ltifrEmployees", "ltifrWorkers", "permEmpTotal", "tempEmpTotal", "permWorkerTotal", "tempWorkerTotal", "totalEmployees"];

    // For annual module, exclude only turnover fields as they are calculated
    if (module === "annual") {
      excludedFields.push(
        "turnoverRatePermEmpMale",
        "turnoverRatePermEmpFemale",
        "turnoverRatePermWorkerMale",
        "turnoverRatePermWorkerFemale"
      );
    }

    Object.entries(formData).forEach(([key, value]) => {
      if (excludedFields.includes(key)) return;

      // Only validate fields relevant to the current module
      if (module === "annual") {
        // Validate annual fields
        const annualFields = [
          "beginningYearPermEmpMale", "beginningYearPermEmpFemale",
          "beginningYearPermWorkerMale", "beginningYearPermWorkerFemale",
          "endYearPermEmpMale", "endYearPermEmpFemale",
          "endYearPermWorkerMale", "endYearPermWorkerFemale",
          "leftYearPermEmpMale", "leftYearPermEmpFemale",
          "leftYearPermWorkerMale", "leftYearPermWorkerFemale"
        ];
        if (annualFields.includes(key) && !value) {
          newErrors[key] = textError;
          hasErrors = true;
        }

        // Validate that 1(a) should not be smaller than 2(a), etc.
        const begEmpMale = parseNumber(formData.beginningYearPermEmpMale);
        const endEmpMale = parseNumber(formData.endYearPermEmpMale);
        const hasBegEmpMale =
          String(formData.beginningYearPermEmpMale ?? "").trim() !== "";
        const hasEndEmpMale =
          String(formData.endYearPermEmpMale ?? "").trim() !== "";
        // if (hasBegEmpMale && hasEndEmpMale && begEmpMale > endEmpMale) {
        //   newErrors.endYearPermEmpMale = "End of year value must be greater than or equal to beginning of year value";
        //   hasErrors = true;
        // }

        const begEmpFemale = parseNumber(formData.beginningYearPermEmpFemale);
        const endEmpFemale = parseNumber(formData.endYearPermEmpFemale);
        const hasBegEmpFemale =
          String(formData.beginningYearPermEmpFemale ?? "").trim() !== "";
        const hasEndEmpFemale =
          String(formData.endYearPermEmpFemale ?? "").trim() !== "";
        // if (hasBegEmpFemale && hasEndEmpFemale && begEmpFemale > endEmpFemale) {
        //   newErrors.endYearPermEmpFemale = "End of year value must be greater than or equal to beginning of year value";
        //   hasErrors = true;
        // }

        const begWorkerMale = parseNumber(formData.beginningYearPermWorkerMale);
        const endWorkerMale = parseNumber(formData.endYearPermWorkerMale);
        const hasBegWorkerMale =
          String(formData.beginningYearPermWorkerMale ?? "").trim() !== "";
        const hasEndWorkerMale =
          String(formData.endYearPermWorkerMale ?? "").trim() !== "";
        // if (hasBegWorkerMale && hasEndWorkerMale && begWorkerMale > endWorkerMale) {
        //   newErrors.endYearPermWorkerMale = "End of year value must be greater than or equal to beginning of year value";
        //   hasErrors = true;
        // }

        const begWorkerFemale = parseNumber(formData.beginningYearPermWorkerFemale);
        const endWorkerFemale = parseNumber(formData.endYearPermWorkerFemale);
        const hasBegWorkerFemale =
          String(formData.beginningYearPermWorkerFemale ?? "").trim() !== "";
        const hasEndWorkerFemale =
          String(formData.endYearPermWorkerFemale ?? "").trim() !== "";
        // if (
        //   hasBegWorkerFemale &&
        //   hasEndWorkerFemale &&
        //   begWorkerFemale > endWorkerFemale
        // ) {
        //   newErrors.endYearPermWorkerFemale = "End of year value must be greater than or equal to beginning of year value";
        //   hasErrors = true;
        // }
      } else {
        // Validate monthly fields
        const monthlyFields = [
          // Section 1: Total Number of Employees
          "permEmpMale", "permEmpDifferentlyAbledMale", "permEmpFemale", "permEmpDifferentlyAbledFemale", "permEmpTotal",
          "tempEmpMale", "tempEmpDifferentlyAbledMale", "tempEmpFemale", "tempEmpDifferentlyAbledFemale", "tempEmpTotal",
          "permWorkerMale", "permWorkerDifferentlyAbledMale", "permWorkerFemale", "permWorkerDifferentlyAbledFemale", "permWorkerTotal",
          "tempWorkerMale", "tempWorkerDifferentlyAbledMale", "tempWorkerFemale", "tempWorkerDifferentlyAbledFemale", "tempWorkerTotal",
          // Section 2-7
          "trainingHours", "avgTrainingHoursPerEmployee", "healthAndSafetyIncidents",
          "ltiEmployees", "ltiWorkers", "workingHoursEmployees", "workingHoursWorkers"
        ];
        if (monthlyFields.includes(key) && !value) {
          newErrors[key] = textError;
          hasErrors = true;
        }
      }
    });
    console.log(newErrors, "values of the fetchData");
    setErrors(newErrors);
    return hasErrors;
  };

  // Next step function following the pattern from Social.jsx
  const nextStep = () => {
    setCurrentStep((prev) => prev + 1);
  };

  // Mark step as complete
  const markStepComplete = () => {
    // Step completion logic if needed
  };

  const handleSubmit = async () => {
    if (validation()) return;

    // Send the appropriate formData based on module
    const dataToSubmit = {
      ...(module === "annual" ? annualFormData : monthlyFormData),
    };

    // Ensure LTIFR values are included for monthly module
    if (module === "monthly") {
      // Recalculate LTIFR values to ensure they're up-to-date before submission
      const workingHoursEmp = parseNumber(monthlyFormData.workingHoursEmployees);
      const ltiEmp = parseNumber(monthlyFormData.ltiEmployees);
      if (workingHoursEmp > 0 && ltiEmp >= 0) {
        dataToSubmit.ltifrEmployees = ((ltiEmp / workingHoursEmp) * 1000000).toFixed(2);
      } else {
        dataToSubmit.ltifrEmployees = monthlyFormData.ltifrEmployees || "0";
      }

      const workingHoursWkr = parseNumber(monthlyFormData.workingHoursWorkers);
      const ltiWkr = parseNumber(monthlyFormData.ltiWorkers);
      if (workingHoursWkr > 0 && ltiWkr >= 0) {
        dataToSubmit.ltifrWorkers = ((ltiWkr / workingHoursWkr) * 1000000).toFixed(2);
      } else {
        dataToSubmit.ltifrWorkers = monthlyFormData.ltifrWorkers || "0";
      }
    }
    const cleanedDataToSubmit = Object.fromEntries(
      Object.entries(dataToSubmit).map(([key, value]) => {
        if (typeof value === "string") return [key, removeCommas(value)];
        return [key, value];
      })
    );

    let payload = {
      employeeWellBeing: cleanedDataToSubmit,
      stepsCompleted:{
        employeeWellBeing: true,
      },
    };

    if (module === "annual") {
      payload.yearToSubmit = year;
    } else {
      payload.yearToSubmit = year;
      payload.monthToSubmit = month;
    }
    await put(`/social/updateSocial?Id=${id}`, payload)
      .then((response) => {
        console.log(response);
        // Use nextStep instead of navigate, following the pattern from Social.jsx
        // nextStep();
        navigate(`/social/data/diversity-and-inclusion/${id}`);
      })
      .catch((error) => {
        if (error?.response?.data?.statusCode === 400) {
          setsnackOpen({
            open: true,
            message: error?.response?.data?.message,
            severity: "error",
          });
        } else {
          setsnackOpen({
            open: true,
            message: error?.response?.data?.message|| "Something went wrong",
            severity: "error",
          });

        }
      });
  };


  // Annual fields configuration
  const annualFieldsConfig = [
    {
      sectionTitle: "1. Employees at the beginning of the year:",
      fields: [
        { label: "(a) Permanent Employee Male (No's)", name: "beginningYearPermEmpMale" },
        { label: "(b) Permanent Employee Female (No's)", name: "beginningYearPermEmpFemale" },
        { label: "(c) Permanent Worker Male (No's)", name: "beginningYearPermWorkerMale" },
        { label: "(d) Permanent Worker Female (No's)", name: "beginningYearPermWorkerFemale" },
      ]
    },
    {
      sectionTitle: "2. Employees at the end of the year:",
      fields: [
        { label: "(a) Permanent Employee Male (No's)", name: "endYearPermEmpMale" },
        { label: "(b) Permanent Employee Female (No's)", name: "endYearPermEmpFemale" },
        { label: "(c) Permanent Worker Male (No's)", name: "endYearPermWorkerMale" },
        { label: "(d) Permanent Worker Female (No's)", name: "endYearPermWorkerFemale" },
      ]
    },
    {
      sectionTitle: "3. Employees left the company during the year:",
      fields: [
        { label: "(a) Permanent Employee Male (No's)", name: "leftYearPermEmpMale",  },
        { label: "(b) Permanent Employee Female (No's)", name: "leftYearPermEmpFemale", },
        { label: "(c) Permanent Worker Male (No's)", name: "leftYearPermWorkerMale",  },
        { label: "(d) Permanent Worker Female (No's)", name: "leftYearPermWorkerFemale", 
          // disabled: true
         },
      ]
    },
    {
      sectionTitle: "4. Employee Turnover Rate:",
      fields: [
        { label: "(a) Permanent Employee Male (%)", name: "turnoverRatePermEmpMale", disabled: true },
        { label: "(b) Permanent Employee Female (%)", name: "turnoverRatePermEmpFemale", disabled: true },
        { label: "(c) Permanent Worker Male (%)", name: "turnoverRatePermWorkerMale", disabled: true },
        { label: "(d) Permanent Worker Female (%)", name: "turnoverRatePermWorkerFemale", disabled: true },
      ]
    },
  ];

  // Monthly fields configuration
  const monthlyFieldsConfig = [
    {
      sectionTitle: "1. Total Number of Employees:",
      subsections: [
        {
          title: "i. Permanent employees",
          fields: [
            { label: "(a) Male (No's)", name: "permEmpMale" },
            { label: "(b) Differently abled Males (No's)", name: "permEmpDifferentlyAbledMale" },
            { label: "(c) Female (No's)", name: "permEmpFemale" },
            { label: "(d) Differently abled Females (No's)", name: "permEmpDifferentlyAbledFemale" },
            { label: "(e) Total (No's)", name: "permEmpTotal", disabled: true },
          ]
        },
        {
          title: "ii. Temporary Employees",
          fields: [
            { label: "(a) Male (No's)", name: "tempEmpMale" },
            { label: "(b) Differently abled Males (No's)", name: "tempEmpDifferentlyAbledMale" },
            { label: "(c) Female (No's)", name: "tempEmpFemale" },
            { label: "(d) Differently abled Females (No's)", name: "tempEmpDifferentlyAbledFemale" },
            { label: "(e) Total (No's)", name: "tempEmpTotal", disabled: true },
          ]
        },
        {
          title: "iii. Permanent Workers",
          fields: [
            { label: "(a) Male (No's)", name: "permWorkerMale" },
            { label: "(b) Differently abled Males (No's)", name: "permWorkerDifferentlyAbledMale" },
            { label: "(c) Female (No's)", name: "permWorkerFemale" },
            { label: "(d) Differently abled Females (No's)", name: "permWorkerDifferentlyAbledFemale" },
            { label: "(e) Total (No's)", name: "permWorkerTotal", disabled: true },
          ]
        },
        {
          title: "iv. Temporary Workers",
          fields: [
            { label: "(a) Male (No's)", name: "tempWorkerMale" },
            { label: "(b) Differently abled Males (No's)", name: "tempWorkerDifferentlyAbledMale" },
            { label: "(c) Female (No's)", name: "tempWorkerFemale" },
            { label: "(d) Differently abled Females (No's)", name: "tempWorkerDifferentlyAbledFemale" },
            { label: "(e) Total (No's)", name: "tempWorkerTotal", disabled: true },
          ]
        },
      ],

    },
    {
      label: "2. Number of Employee Training Hours:",
      name: "trainingHours",
      value: formData.trainingHours,
      onChange: handleNumberChange,
      placeholder: "Enter Number",
      disabled: false,
      width: "49.5%"
    },
    {
      label: "3. Average Training Hours per Employee:",
      name: "avgTrainingHoursPerEmployee",
      value: formData.avgTrainingHoursPerEmployee,
      onChange: handleNumberChange,
      placeholder: "Enter Number",
      disabled: false,
      width: "49.5%"
    },
    {
      label: "4. Number of Health and Safety Incidents:",
      name: "healthAndSafetyIncidents",
      value: formData.healthAndSafetyIncidents,
      onChange: handleNumberChange,
      placeholder: "Enter Number",
      disabled: false,
      width: "49.5%"
    },
    {
      sectionTitle: "5. Number of Lost Time Injuries (LTI):",
      fields: [
        { label: "(a) LTI of Employees (hours)", name: "ltiEmployees" },
        { label: "(c) LTI of Workers (hours)", name: "ltiWorkers" },
      ]
    },
    {
      sectionTitle: "6. Total working hours in the month:",
      fields: [
        { label: "(a) Working hours of Employees", name: "workingHoursEmployees" },
        { label: "(c) Working hours of Workers", name: "workingHoursWorkers" },
      ]
    },
    {
      sectionTitle: "7. Monthly Lost Time Injury Frequency Rate (LTIFR):",
      fields: [
        { label: "(a) LTIFR of Employees", name: "ltifrEmployees", disabled: true },
        { label: "(c) LTIFR for Workers", name: "ltifrWorkers", disabled: true },
      ]
    },
  ];

  // Conditionally render based on current step, following the pattern from Social.jsx
  if (currentStep === 2) {
    // Render Diversity component for step 2
    const entryTypeParam = module === "annual" ? "annual" : "monthly";
    return <Diversity />;
  }

  return (
    <MainDashboardLayout sidebar={<SocialLayout />}>
      <MessagePopup {...{ snackOpen, setsnackOpen }} />
      {/* Module Selection and Year */}
      {/* <Box sx={{ mb: 3, display: "flex", flexDirection: "column", gap: 2 }}> */}
        {/* <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>

          </Typography>
          <RadioGroup
            value={module}
            onChange={handleModuleChange}
            sx={{ display: "flex", flexDirection: "row", gap: 2 }}
          >
            <FormControlLabel
              value="monthly"
              control={<Radio sx={socialRadioSx} />}
              label="Monthly Module"
              disabled={module === "annual"}
            />
            <FormControlLabel
              value="annual"
              control={<Radio sx={socialRadioSx} />}
              label="Annual Module"
              disabled={module === "monthly"}
            />
          </RadioGroup>
        </Box> */}

        {/* {module === "monthly" && (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 200px" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: "-0.5rem" }}>
                Select Month
              </Typography>
              <SelectField
                name="Month"
                value={month}
                handleChange={(e) => {
                  setMonth(e.target.value);
                  setDisableValue("");
                  const selectedMonthIndex = Number(e.target.value);
                  if (selectedMonthIndex > currentMonth + 1) {
                    setDisableValue(currentYear);
                    if (year === currentYear.toString()) {
                      setYear("");
                    }
                  }
                }}
                label="Month"
                field="month"
                errors={errors?.["month"] || ""}
                defaultValue="Tap to select"
                options={monthlist}
              />
            </Box>
            <Box sx={{ flex: "1 1 200px" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: "-0.5rem" }}>
                Select Year
              </Typography>
              <SelectField
                name="Year"
                value={year}
                handleChange={(e) => {
                  setYear(e.target.value);
                  setErrors((prev) => ({ ...prev, year: "" }));
                }}
                options={filteredYears}
                errors={errors?.["year"] || ""}
                defaultValue="Tap to select"
                menuDisable={disableValue}
              />
            </Box>
          </Box>
        )}

        {module === "annual" && (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: "-0.5rem" }}>
              Select Year
            </Typography>
            <SelectField
              name="Year"
              value={year}
              handleChange={(e) => setYear(e.target.value)}
              options={years}
              errors={errors?.["year"] || ""}
              defaultValue="Tap to select"
              width="49%"
            />
          </Box>
        )} */}
      {/* </Box> */}

      <div
        style={{ display: "flex", flexDirection: "column", margin: "1.2% 0" }}
      >
        <p className={styles.heading + " " + styles.form_headingStrongUpper}>A. Employee Well-Being</p>


        {/* Conditionally render based on module type */}
        {module === "annual" ? (
          // Annual Module Fields
          annualFieldsConfig.map((section, sectionIndex) => (
            <Box key={sectionIndex} sx={{ mb: 3 }}>   
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
                {section.sectionTitle}
              </Typography>
              <Grid container spacing={3} mr={2} >
                {section.fields.map((field, fieldIndex) => (
                  <Grid item xs={12} sm={6} key={fieldIndex} width="50%">
                    <NumericInputField
                      label={field.label}
                      name={field.name}
                      value={formData[field.name] || ""}
                      errors={errors}
                      onChange={handleNumberChange}
                      placeholder="Enter Number"
                      disabled={field.disabled || false}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))
        ) : (
          // Monthly Module Fields
          monthlyFieldsConfig.map((item, index) => {
            // If it's a section with subsections (like Section 1)
            if (item.sectionTitle && item.subsections) {
              // Special handling for first section - show title with inline input
              if (index === 0 && module === "monthly") {
                return (
                  <Box key={index} sx={{ mb: 3 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                        {item.sectionTitle}
                      </Typography>
                      <TextField
                        name="totalEmployees"
                        value={formData.totalEmployees || ""}
                        variant="outlined"
                        type="text"
                        size="small"
                        sx={{ width: "200px" }}
                        placeholder="Auto-calculated"
                        disabled={true}
                        error={!!errors?.totalEmployees}
                      />
                      {errors?.totalEmployees && (
                        <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                          {errors?.totalEmployees}
                        </FormHelperText>
                      )}
                    </div>
                    {item.subsections.map((subsection, subIndex) => (
                      <Box key={subIndex} sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                          {subsection.title}
                        </Typography>
                        <Grid container spacing={2}>
                          {subsection.fields.map((field, fieldIndex) => {
                            // Calculate percentage for fields (a), (b), (c), (d) - not for (e) total
                            let percentage = "";
                            if (!field.disabled && field.name !== "permEmpTotal" && field.name !== "tempEmpTotal" && field.name !== "permWorkerTotal" && field.name !== "tempWorkerTotal") {
                              // Determine which total to use based on the field name
                              let totalField = "";
                              if (field.name.startsWith("permEmp")) {
                                totalField = "permEmpTotal";
                              } else if (field.name.startsWith("tempEmp")) {
                                totalField = "tempEmpTotal";
                              } else if (field.name.startsWith("permWorker")) {
                                totalField = "permWorkerTotal";
                              } else if (field.name.startsWith("tempWorker")) {
                                totalField = "tempWorkerTotal";
                              }

                              if (totalField) {
                                const fieldValue = parseNumber(formData[field.name]);
                                const totalValue = parseNumber(formData[totalField]);
                                if (totalValue > 0 && fieldValue > 0) {
                                  percentage = ((fieldValue / totalValue) * 100).toFixed(2) + "%";
                                } else {
                                  percentage = "0%";
                                }
                              }
                            }

                            return (
                              <Grid item xs={12} sm={6} key={fieldIndex}>
                                <NumericInputField
                                  label={field.label}
                                  name={field.name}
                                  value={formData[field.name] || ""}
                                  errors={errors}
                                  onChange={handleNumberChange}
                                  placeholder="Enter Number"
                                  disabled={field.disabled || false}
                                  endAdornment={percentage ? <InputAdornment position="end">{percentage}</InputAdornment> : null}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    ))}
                    {/* Total Employees Field: i(e) + ii(e) + iii(e) + iv(e) */}
                    {item.totalField && (
                      <Box sx={{ mt: 2 }}>
                        <NumericInputField
                          label={item.totalField.label}
                          name={item.totalField.name}
                          value={formData[item.totalField.name] || ""}
                          errors={errors}
                          onChange={handleNumberChange}
                          placeholder="Auto-calculated"
                          disabled={item.totalField.disabled || false}
                          width="49.5%"
                        />
                      </Box>
                    )}
                  </Box>
                );
              }

              // Regular section rendering (not first section or not monthly)
              return (
                <Box key={index} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
                    {item.sectionTitle}
                  </Typography>
                  {item.subsections.map((subsection, subIndex) => (
                    <Box key={subIndex} sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                        {subsection.title}
                      </Typography>
                      <Grid container spacing={2}>
                        {subsection.fields.map((field, fieldIndex) => {
                          // Calculate percentage for fields (a), (b), (c), (d) - not for (e) total
                          let percentage = "";
                          if (!field.disabled && field.name !== "permEmpTotal" && field.name !== "tempEmpTotal" && field.name !== "permWorkerTotal" && field.name !== "tempWorkerTotal") {
                            // Determine which total to use based on the field name
                            let totalField = "";
                            if (field.name.startsWith("permEmp")) {
                              totalField = "permEmpTotal";
                            } else if (field.name.startsWith("tempEmp")) {
                              totalField = "tempEmpTotal";
                            } else if (field.name.startsWith("permWorker")) {
                              totalField = "permWorkerTotal";
                            } else if (field.name.startsWith("tempWorker")) {
                              totalField = "tempWorkerTotal";
                            }

                            if (totalField) {
                              const fieldValue = parseNumber(formData[field.name]);
                              const totalValue = parseNumber(formData[totalField]);
                              if (totalValue > 0 && fieldValue > 0) {
                                percentage = ((fieldValue / totalValue) * 100).toFixed(2) + "%";
                              } else {
                                percentage = "0%";
                              }
                            }
                          }

                          return (
                            <Grid item xs={12} sm={6} key={fieldIndex}>
                              <NumericInputField
                                label={field.label}
                                name={field.name}
                                value={formData[field.name] || ""}
                                errors={errors}
                                onChange={handleNumberChange}
                                placeholder="Enter Number"
                                disabled={field.disabled || false}
                                endAdornment={percentage ? <InputAdornment position="end">{percentage}</InputAdornment> : null}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  ))}
                </Box>
              );
            }
            // If it's a section with fields array (like Section 5, 6, 7)
            if (item.sectionTitle && item.fields) {
              return (
                <Box key={index} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
                    {item.sectionTitle}
                  </Typography>
                  <Grid container spacing={2}>
                    {item.fields.map((field, fieldIndex) => (
                      <Grid item xs={12} sm={6} key={fieldIndex}>
                        <NumericInputField
                          label={field.label}
                          name={field.name}
                          value={formData[field.name] || ""}
                          errors={errors}
                          onChange={handleNumberChange}
                          placeholder="Enter Number"
                          disabled={field.disabled || false}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            }
            // Regular single field (like Section 2, 3, 4)
            return (
              <NumericInputField
                key={index}
                label={item.label}
                name={item.name}
                value={item.value}
                errors={errors}
                onChange={item.onChange}
                placeholder={item.placeholder}
                disabled={item.disabled}
                width={item.width || "100%"}
              />
            );
          })
        )}

        {/* Submit Button */}
        <div style={{ textAlign: "right", marginBottom: "2rem" }}>
          <Button
            variant="contained"
            className={styles.submit_button}
            onClick={handleSubmit}
            sx={primaryButtonSx}
          >
            Save & Continue <ArrowForwardIcon sx={{ ml: 1 }} />
          </Button>
        </div>
      </div>
    </MainDashboardLayout>
  );
};

export default WellBeing;
